import {
	Card,
	CardBody,
	CardHeader,
	Flex,
	FlexProps,
	Heading,
	Text,
} from "@chakra-ui/react";

import { PaymentStatus as PaymentStatusChartType } from "../types/payment-status-chart-data";
import { Dot } from "./dot";
import {
	Bar,
	XAxis,
	YAxis,
	Tooltip,
	CartesianGrid,
	ResponsiveContainer,
	TooltipProps,
} from "recharts";
import { NameType } from "recharts/types/component/DefaultTooltipContent";
import { BarChart } from "recharts";
import { ValueType } from "recharts/types/component/DefaultTooltipContent";
import { PAYMENT_STATUS_COLOR } from "../types/payment-status-color";

interface PaymentStatusChartProps extends FlexProps {
	chart: PaymentStatusChartType;
}

export function PaymentStatusChart({
	chart,
	...props
}: PaymentStatusChartProps) {
	const statuses = chart.data.flatMap((data) =>
		Object.keys(data).filter((key) => key !== "title")
	);

	const uniqueStatuses = [...new Set(statuses)];

	function getColor(name: string) {
		return PAYMENT_STATUS_COLOR[name as keyof typeof PAYMENT_STATUS_COLOR];
	}

	function CustomTooltip({
		active,
		payload,
		label,
	}: TooltipProps<ValueType, NameType>) {
		if (active && payload && payload.length) {
			return (
				<Flex
					bgColor="#302E34"
					borderRadius="8px"
					px={4}
					py={2}
					whiteSpace="nowrap"
					direction="column"
					width="180px"
					gap={1}
				>
					{payload.map((status) => {
						return (
							<Flex
								key={status.name}
								direction="row"
								gap={1}
								alignItems="center"
							>
								<Dot color={getColor(status.name as string)} />
								<Text
									flex="1"
									color="#fff"
									textTransform="capitalize"
									fontSize="14px"
									fontWeight={400}
								>
									{status.name}
								</Text>
								<Text color="#fff" fontSize="14px" fontWeight={400}>
									{status.value}
								</Text>
							</Flex>
						);
					})}
				</Flex>
			);
		}
		return null;
	}

	return (
		<Card {...props}>
			<CardHeader>
				<Flex direction="column" gap={2}>
					<Heading fontSize="18px" fontWeight="600">
						Payment Status
					</Heading>
					<Flex direction="row" gap={3}>
						{uniqueStatuses.map((status) => (
							<Flex key={status} direction="row" gap={2} alignItems="center">
								<Dot color={getColor(status)} />
								<Text
									fontWeight={500}
									fontSize="12px"
									color="#2E2C32"
									textTransform="capitalize"
								>
									{status}
								</Text>
							</Flex>
						))}
					</Flex>
				</Flex>
			</CardHeader>
			<CardBody>
				<ResponsiveContainer width="100%" height={400}>
					<BarChart data={chart.data} maxBarSize={205}>
						<CartesianGrid vertical={false} />
						<XAxis
							dataKey="title"
							axisLine={false}
							tickLine={false}
							tickMargin={15}
						/>
						<YAxis tickLine={false} axisLine={false} />
						<Tooltip
							content={<CustomTooltip />}
							wrapperStyle={{ zIndex: 1 }}
						/>
						{uniqueStatuses.map((status) => (
							<Bar
								key={status}
								dataKey={status}
								stackId="a"
								fill={getColor(status)}
							/>
						))}
					</BarChart>
				</ResponsiveContainer>
			</CardBody>
		</Card>
	);
}
